import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLogin } from '../hooks/useLogin';

export const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    formData,
    setFormData,
    isLoading,
    isLocked,
    handleSubmit,
    error,
    getRemainingTime,
    totpRequired,
  } = useLogin();

  useEffect(() => {
    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName.includes('mui-auto-fill')) {
        const input = e.target as HTMLInputElement;
        if (input.name === 'username') {
          setFormData(prev => ({ ...prev, username: input.value }));
        } else if (input.name === 'password') {
          setFormData(prev => ({ ...prev, password: input.value }));
        }
      }
    };

    document.addEventListener('animationstart', handleAnimationStart);
    return () => {
      document.removeEventListener('animationstart', handleAnimationStart);
    };
  }, [setFormData]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete="off"
      sx={{
        width: '100%',
        mt: 0,
      }}
    >
      <TextField
        margin="normal"
        required
        fullWidth
        label="Имя пользователя"
        name="username"
        autoComplete="username"
        autoFocus
        value={formData.username}
        onChange={(e) =>
          setFormData({ ...formData, username: e.target.value })
        }
        disabled={isLoading || isLocked}
        InputLabelProps={{
          shrink: true
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        label="Пароль"
        name="password"
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        value={formData.password}
        onChange={(e) =>
          setFormData({ ...formData, password: e.target.value })
        }
        disabled={isLoading || isLocked}
        InputLabelProps={{
          shrink: true
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />
      {totpRequired && (
        <>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Код 2FA или резервный код"
            name="totpCode"
            autoComplete="one-time-code"
            inputMode="numeric"
            value={formData.totpCode}
            onChange={(e) => setFormData({ ...formData, totpCode: e.target.value })}
            disabled={isLoading || isLocked}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={formData.trustDevice}
                onChange={(e) => setFormData({ ...formData, trustDevice: e.target.checked })}
              />
            )}
            label="Запомнить это устройство на 30 дней"
            sx={{ mt: 0.5 }}
          />
        </>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || !formData.username || !formData.password || (totpRequired && !formData.totpCode) || isLocked}
        sx={{
          mt: 2,
          mb: 1,
          py: 1.2,
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 'none',
            bgcolor: 'primary.dark',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Войти'}
      </Button>
    </Box>
  );
}; 
